import { InventoryCategoryName } from '~/constants/inventoryCategory'

/**
 * Required receiving attributes by product category and controlled substance.
 * Used to enforce validation and show asterisk (*) on required fields in Order Item Receipts.
 */

export const RECEIPT_FIELD_KEYS = [
  'receivedQuantity',
  'lotNumber',
  'expirationDate',
  'serialNumber',
  'businessReferenceNumber',
  'notes',
] as const

export type ReceiptFieldKey = (typeof RECEIPT_FIELD_KEYS)[number]

/**
 * Required fields per category (when not controlled).
 * Controlled: when category is Drugs and controlled flag is true, use CONTROLLED_DRUGS_REQUIRED instead.
 */
const REQUIRED_BY_CATEGORY: Record<string, ReceiptFieldKey[]> = {
  [InventoryCategoryName.DRUG]: [
    'receivedQuantity',
    'lotNumber',
    'expirationDate',
  ],
  [InventoryCategoryName.VACCINES]: [
    'receivedQuantity',
    'lotNumber',
    'expirationDate',
    'serialNumber',
  ],
  [InventoryCategoryName.SUPPLIES]: ['receivedQuantity'],
  [InventoryCategoryName.VET_DIET]: [
    'receivedQuantity',
    'lotNumber',
    'expirationDate',
  ],
  [InventoryCategoryName.FOOD]: [
    'receivedQuantity',
    'lotNumber',
    'expirationDate',
  ],
}

/** When controlled is true and category is Drugs (Practice Reference required). */
const CONTROLLED_DRUGS_REQUIRED: ReceiptFieldKey[] = [
  'receivedQuantity',
  'lotNumber',
  'expirationDate',
  'businessReferenceNumber',
]

/**
 * Returns the list of required receipt field keys for the given category and controlled flag.
 * Uses category name (inventoryItem.category.name). Unknown category falls back to Drugs required set.
 */
export function getRequiredReceiptFields(
  categoryName: string | null | undefined,
  controlled: boolean | null | undefined,
): ReceiptFieldKey[] {
  const name = categoryName?.trim()
  if (controlled && name === InventoryCategoryName.DRUG) {
    return CONTROLLED_DRUGS_REQUIRED
  }
  if (name && name in REQUIRED_BY_CATEGORY) {
    return REQUIRED_BY_CATEGORY[name]
  }
  return REQUIRED_BY_CATEGORY[InventoryCategoryName.DRUG]
}
